import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export type CalendarioTipoEvento = 'prazo' | 'visita_tecnica' | 'abertura_proposta' | 'outro';

interface LicitacaoResumo {
  objeto: string;
  orgao: string | null;
  uf: string | null;
  valorEstimado: number | null;
  prazoAbertura: string | null;
  modalidade: string | null;
}

export interface CalendarioEvento {
  id: number;
  empresaId: number;
  empresaNome: string;
  licitacaoId: number;
  dataEvento: string;
  tipoEvento: CalendarioTipoEvento;
  observacoes: string | null;
  createdAt: string;
  updatedAt: string;
  licitacao: LicitacaoResumo;
}

export interface CalendarioParticipando {
  id: number;
  empresaId: number;
  empresaNome: string;
  licitacaoId: number;
  etapa: string;
  observacoes: string | null;
  createdAt: string;
  updatedAt: string;
  licitacao: LicitacaoResumo;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export interface CalendarioGeralFiltros {
  empresaIds?: number[];
  uf?: string;
  categoria?: string;
  modalidade?: string;
}

function buildGeralParams(de: string, ate: string, filtros?: CalendarioGeralFiltros) {
  const params: Record<string, string> = { de, ate };
  if (filtros?.empresaIds && filtros.empresaIds.length > 0) params.empresa_ids = filtros.empresaIds.join(',');
  if (filtros?.uf) params.uf = filtros.uf;
  if (filtros?.categoria) params.categoria = filtros.categoria;
  if (filtros?.modalidade) params.modalidade = filtros.modalidade;
  return params;
}

export function useCalendarioGeral(de: string, ate: string, filtros?: CalendarioGeralFiltros) {
  return useQuery({
    queryKey: ['calendario-geral', de, ate, filtros],
    queryFn: async () => {
      const response = await api.get<ApiResponse<{ vinculadas: CalendarioEvento[]; participando: CalendarioParticipando[] }>>(
        '/empresas/calendario/geral',
        { params: buildGeralParams(de, ate, filtros) }
      );
      return response.data;
    },
  });
}

export function useCriarEventoCalendario(empresaId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { licitacaoId: number; dataEvento: string; tipoEvento?: CalendarioTipoEvento; observacoes?: string }) => {
      const response = await api.post<ApiResponse<CalendarioEvento>>(`/empresas/${empresaId}/calendario`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendario-geral'] });
    },
  });
}

export function useAtualizarEventoCalendario(empresaId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventoId, ...payload }: { eventoId: number; dataEvento?: string; tipoEvento?: CalendarioTipoEvento; observacoes?: string }) => {
      const response = await api.patch<ApiResponse<CalendarioEvento>>(`/empresas/${empresaId}/calendario/${eventoId}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendario-geral'] });
    },
  });
}

export function useExcluirEventoCalendario(empresaId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (eventoId: number) => {
      await api.delete(`/empresas/${empresaId}/calendario/${eventoId}`);
      return eventoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendario-geral'] });
    },
  });
}
